import {forwardRef, useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {useSwipeable} from 'react-swipeable'

import Next from '@mui/icons-material/SkipNext'
import Pause from '@mui/icons-material/Pause'
import Dialog from '@mui/material/Dialog'
import Skeleton from '@mui/material/Skeleton'
import Previous from '@mui/icons-material/SkipPrevious'
import PlayArrow from '@mui/icons-material/PlayArrow'
import IconButton from '@mui/material/IconButton'

import {SiApplemusic} from 'react-icons/si'
// import {PiSpotifyLogoFill} from 'react-icons/pi'

import MediaPlayerExpanded from './MediaPlayerExpanded'

import {Image, SlideTransition, FavoriteIconButton} from '../generics'



const styles = {
  root: css`
    background-color: #242639;
    display: flex;
    align-items: center;
    padding: 15px 5px 15px 15px;
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
    width: 100%;
    box-shadow: 0px 10px 20px -3px rgba(0, 0, 0, 0.8);
  `,
  imageContainer: css`
    width: 44px;
    height: 44px;
    border-radius: 7px;
    margin-right: 10px;
    flex-shrink: 0;
    position: relative;
  `,
  image: css`
    width: 44px;
    height: 44px;
    border-radius: 7px;
    object-fit: cover;
  `,
  musicServiceIcon: css`
    position: absolute;
    z-index: 10;
    width: 22px;
    height: 22px;
    bottom: -9px;
    right: -7px;
    color: #fc3c44;
    background-color: #fff;
    border-radius: 50%;
  `,
  infoContainer: css`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-left: 10px;

    h3 {
      color: #fff;
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.2;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }


    p {
      font-size: 0.9rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: grey;
      line-height: 1.2;
    }
  `,
  infoTitle: css`
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.1;
    padding-right: 5px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #fff;


  `,
  infoSubtitle: css`
    font-size: 0.9rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: #fff;
  `,
  controls: css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 10px;
    gap: 2px;
  `,
  controlButton: css`
    width: 35px;
    height: 35px;
  `,
  playButton: css`
    width: 40px;
    height: 40px;
    background-color: #242639;
    color: #fff;
    padding: 10px;
    border: none;
    outline: none;
    border-radius: 50%;
    transition: transform 0.2s ease-in-out, box-shadow 0.3s ease-in-out;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.3), 0px 0px 5px rgba(255, 0, 149, 0.6);
    cursor: pointer;

    &:active {
      transform: scale(0.95);
      box-shadow: inset 5px 5px 10px rgba(0, 0, 0, 0.6), inset 0px 0px 5px rgba(255, 0, 149, 1);
    }
  `,
}




const MediaPlayerMobile = forwardRef((props, ref) => {
  
  const [isExpanded, setIsExpanded] = useState(false)
  

  const {
    track,
    volume,
    setVolume,
    imageRef,
    audioRef,
    isPlaying,
    isMuted,
    setIsMuted,
    handleClose,
    playNextTrack,
    handlePlayClick,
    playPreviousTrack,
    fetchCkordIdAndType
  } = props


  const {
    name, 
    image, 
    composer, 
    isVideoTrack,
    // isSpotifyTrack,
    isAppleMusicTrack,
    imageDerivatives 
  } = track?.attributes || {} 



  const handlers = useSwipeable({
    onSwipedUp: expandPlayer,
    onSwipedDown: handleClose
  })



  function expandPlayer() {
    setIsExpanded(true)
  }



  function minimizePlayer() {
    setIsExpanded(false)

    if (isVideoTrack) {
      playNextTrack()
    }
  }



  function handleExpandedClose() {
    minimizePlayer()
    setTimeout(handleClose, 300)
  }



  return (
    <div ref={ref} css={styles.root} {...handlers}>      
      <div css={styles.imageContainer}>
        <Image 
          id='audio-player-image'
          ref={imageRef}
          src={imageDerivatives?.small || image} 
          alt='track' 
          css={styles.image}
        />

        {isAppleMusicTrack &&
          <SiApplemusic css={styles.musicServiceIcon} /> 
        }
      </div>


      <div css={styles.infoContainer} onClick={expandPlayer}>
        <h3>
          {Boolean(name) ? name : 
            <Skeleton sx={{fontSize: '1rem', bgcolor: '#d3d3d3'}} width={150} 
            />
          }
        </h3>
        
        <p>
          {(Boolean(name) || Boolean(composer)) ? composer : 
            <Skeleton sx={{fontSize: '0.9rem', bgcolor: '#d3d3d3'}} width={100} />
          }
        </p>
      </div>



      {!isAppleMusicTrack &&
        <FavoriteIconButton id={track.id} type='track' size='small' color='#fff' />
      }


      <div css={styles.controls}>
        <IconButton css={styles.button} onClick={playPreviousTrack}>
          <Previous color='secondary' />
        </IconButton>


        <IconButton css={styles.playButton} size='large' onClick={handlePlayClick}>
          {isPlaying 
            ? <Pause fontSize='small' /> 
            : <PlayArrow fontSize='small' />
          }
        </IconButton>


        <IconButton css={styles.button} onClick={playNextTrack}>
          <Next color='secondary' />
        </IconButton>
      </div>



      <Dialog 
        open={isExpanded}
        fullScreen={true}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        sx={{
         '& .MuiDialog-container': {
            maxHeight: '100%',
            height: '100%',
            overflow: 'hidden',

            '& .MuiPaper-root': {                    
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxHeight: '100%',
              height: '100%',
              overflow: 'hidden',
              backgroundColor: '#242639'
            }
          }
        }}>
        <MediaPlayerExpanded
          track={track}
          imageRef={imageRef}
          audioRef={audioRef}
          isPlaying={isPlaying}
          isMuted={isMuted}
          volume={volume}
          setVolume={setVolume}
          setIsMuted={setIsMuted}
          handleClose={handleExpandedClose}
          minimizePlayer={minimizePlayer}
          isMobileDevice={true}
          playNextTrack={playNextTrack}
          handlePlayClick={handlePlayClick}
          playPreviousTrack={playPreviousTrack}
          fetchCkordIdAndType={fetchCkordIdAndType}
        />
      </Dialog>
    </div>
  )
})




export default MediaPlayerMobile




